import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import CustomerFormDrawer from "@/components/CustomerFormDrawer";
import CustomerCsvIO from "@/components/CustomerCsvIO";
import BranchFilter from "@/components/BranchFilter";
import {
  Plus,
  PencilSimple,
  Trash,
  User,
  MagnifyingGlass,
  CheckCircle,
  Lock,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

function ActivityBadge({ status }) {
  const active = status !== "won";
  return active ? (
    <span
      data-testid="customer-status-active"
      className="inline-flex items-center gap-1 px-2 py-0.5 border border-[#00B341] bg-[#00B341]/10 text-[#00B341] text-[10px] font-mono uppercase tracking-wider"
    >
      <CheckCircle size={10} weight="bold" />
      Active
    </span>
  ) : (
    <span
      data-testid="customer-status-inactive"
      className="inline-flex items-center gap-1 px-2 py-0.5 border border-canvas-line bg-canvas-panel text-ink-subtle text-[10px] font-mono uppercase tracking-wider"
    >
      <Lock size={10} weight="bold" />
      Inactive
    </span>
  );
}

function CustomerRow({ customer, isAdmin, onEdit, onDelete }) {
  return (
    <tr
      data-testid={`customer-row-${customer.id}`}
      className="border-b border-canvas-line hover:bg-canvas-panel transition-colors"
    >
      <td className="px-4 py-3">
        <Link
          to={`/customers/${customer.id}`}
          className="text-sm font-semibold text-ink hover:underline"
          data-testid={`customer-name-link-${customer.id}`}
        >
          {customer.name}
        </Link>
        {customer.relation_name && (
          <div className="font-mono text-[10px] text-ink-subtle mt-0.5">
            {customer.relation_type === "s_o"
              ? "S/o"
              : customer.relation_type === "w_o"
              ? "W/o"
              : customer.relation_type === "d_o"
              ? "D/o"
              : customer.relation_type === "represented_by"
              ? "Rep. by"
              : ""}{" "}
            {customer.relation_name}
          </div>
        )}
      </td>
      <td className="px-4 py-3 font-mono text-xs text-ink-muted">{customer.phone}</td>
      <td className="px-4 py-3 font-mono text-[11px] text-ink-muted truncate max-w-[260px]">
        {[customer.town_village, customer.district, customer.state].filter(Boolean).join(", ") || "—"}
      </td>
      <td className="px-4 py-3">
        <ActivityBadge status={customer.status} />
      </td>
      <td className="px-4 py-3 font-mono text-[11px] text-ink-muted">
        {fmtDate(customer.created_at)}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1 justify-end">
          <button
            onClick={() => onEdit(customer)}
            data-testid={`edit-customer-${customer.id}`}
            className="p-1.5 border border-canvas-line hover:border-ink"
          >
            <PencilSimple size={13} weight="bold" />
          </button>
          {isAdmin && (
            <button
              onClick={() => onDelete(customer)}
              data-testid={`delete-customer-${customer.id}`}
              className="p-1.5 border border-canvas-line hover:border-destructive hover:text-destructive"
            >
              <Trash size={13} weight="bold" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

function CustomerFilters({ search, setSearch, activityFilter, setActivityFilter, branchFilter, setBranchFilter, onSearchSubmit }) {
  return (
    <div className="bg-canvas-card border border-canvas-line p-4 flex flex-wrap items-center gap-3 mb-6">
      <div className="flex-1 min-w-[200px] relative">
        <MagnifyingGlass
          size={14}
          weight="bold"
          className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted"
        />
        <input
          data-testid="customer-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onSearchSubmit()}
          placeholder="Search name, mobile, or email…"
          className="w-full pl-9 pr-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
        />
      </div>
      <BranchFilter value={branchFilter} onChange={setBranchFilter} testId="customer-branch-filter" />
      <div className="flex border border-canvas-line">
        {[
          { v: "all", l: "All" },
          { v: "active", l: "Active" },
          { v: "inactive", l: "Inactive" },
        ].map((opt) => (
          <button
            key={opt.v}
            type="button"
            onClick={() => setActivityFilter(opt.v)}
            data-testid={`filter-activity-${opt.v}`}
            className={`px-3 py-2 text-xs font-mono uppercase tracking-wider ${
              activityFilter === opt.v
                ? "bg-ink text-white"
                : "text-ink hover:bg-canvas-panel"
            }`}
          >
            {opt.l}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function CustomersPage() {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [activityFilter, setActivityFilter] = useState("active");
  const [branchFilter, setBranchFilter] = useState("");
  // Server-side pagination — we ask Mongo for a page at a time instead of
  // shipping ~4 000 rows on every render. "Load more" grows the visible
  // window by re-hitting the API with skip = current length.
  const PAGE_SIZE = 100;
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  const buildParams = useCallback(() => {
    const params = {};
    if (search) params.search = search;
    if (activityFilter === "active") params.active_only = true;
    if (activityFilter === "inactive") params.status = "won";
    if (branchFilter) params.branch_id = branchFilter;
    return params;
  }, [search, activityFilter, branchFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    const params = { ...buildParams(), limit: PAGE_SIZE, skip: 0 };
    try {
      const res = await api.get("/customers", { params });
      setCustomers(res.data);
      const t = Number(res.headers?.["x-total-count"]);
      setTotal(Number.isFinite(t) ? t : res.data.length);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  const loadMore = useCallback(async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const params = { ...buildParams(), limit: PAGE_SIZE, skip: customers.length };
      const res = await api.get("/customers", { params });
      setCustomers((prev) => [...prev, ...res.data]);
      const t = Number(res.headers?.["x-total-count"]);
      if (Number.isFinite(t)) setTotal(t);
    } finally {
      setLoadingMore(false);
    }
  }, [buildParams, customers.length, loadingMore]);

  useEffect(() => {
    load();
  }, [activityFilter, branchFilter, load]);

  const openCreate = () => {
    setEditing(null);
    setShowForm(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setShowForm(true);
  };
  const handleSaved = () => {
    setShowForm(false);
    load();
  };
  const handleDelete = async (c) => {
    if (!window.confirm(`Delete customer ${c.name}?`)) return;
    try {
      await api.delete(`/customers/${c.id}`);
      toast.success("Customer deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="03 / Customers"
        title="Customer Registry"
        subtitle="People entered for a booking or sale. Once a sale completes, the record becomes inactive."
        actions={
          <div className="flex items-center gap-2">
            <CustomerCsvIO onImported={load} />
            <button
              onClick={openCreate}
              data-testid="add-customer-button"
              className="bg-ink text-white px-4 py-2.5 text-sm font-semibold tracking-tight hover:bg-brand transition-colors duration-150 flex items-center gap-2"
            >
              <Plus size={14} weight="bold" />
              Add Customer
            </button>
          </div>
        }
      />

      <div className="px-8 py-6">
        <div className="flex items-center justify-between gap-4 mb-3">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-ink-muted">
            {total > customers.length
              ? `showing ${customers.length} of ${total} customer${total === 1 ? "" : "s"}`
              : `${customers.length} customer${customers.length === 1 ? "" : "s"}`}
          </div>
        </div>

        <CustomerFilters
          search={search}
          setSearch={setSearch}
          activityFilter={activityFilter}
          setActivityFilter={setActivityFilter}
          branchFilter={branchFilter}
          setBranchFilter={setBranchFilter}
          onSearchSubmit={load}
        />

        <div className="bg-canvas-card border border-canvas-line overflow-hidden">
          <table className="w-full" data-testid="customers-table">
            <thead>
              <tr className="border-b border-canvas-line bg-canvas-panel">
                {["Customer", "Mobile", "Location", "Status", "Added", ""].map((h) => (
                  <th
                    key={h}
                    className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center font-mono text-xs text-ink-subtle">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && customers.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center">
                    <User size={24} weight="bold" className="text-ink-subtle mx-auto mb-2" />
                    <div className="font-mono text-xs text-ink-subtle mb-3">No customers match.</div>
                    <button
                      onClick={openCreate}
                      className="text-sm font-semibold text-brand hover:underline"
                    >
                      + Add your first customer
                    </button>
                  </td>
                </tr>
              )}
              {customers.map((c) => (
                <CustomerRow
                  key={c.id}
                  customer={c}
                  isAdmin={user?.role === "admin"}
                  onEdit={openEdit}
                  onDelete={handleDelete}
                />
              ))}
              {customers.length < total && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center bg-canvas-panel/40">
                    <button
                      type="button"
                      onClick={loadMore}
                      disabled={loadingMore}
                      data-testid="customers-load-more"
                      className="px-4 py-2 border border-canvas-line text-xs font-mono uppercase tracking-wider hover:bg-canvas-panel disabled:opacity-50"
                    >
                      {loadingMore ? "Loading…" : `Load more · showing ${customers.length} of ${total}`}
                    </button>
                  </td>
                </tr>
              )}
              {!loading && customers.length > 0 && customers.length >= total && total > 100 && (
                <tr>
                  <td colSpan={6} className="px-4 py-3 text-center bg-canvas-panel/20 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-subtle">
                    End of registry · {total} customers
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <CustomerFormDrawer
        open={showForm}
        editing={editing}
        onClose={() => setShowForm(false)}
        onSaved={handleSaved}
      />
    </div>
  );
}
